import React, { useState } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  Loader2, Cloud, Database, RefreshCw, BarChart3, Sparkles,
  CheckCircle2, AlertTriangle, ChevronDown, ChevronUp
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Panel admin de "1 click" para operaciones grandes:
 *  1. Migrar videos viejos a R2
 *  2. Backfill exercise_id en planes viejos
 *  3. Propagar videos a todos los planes
 *  4. Ver cobertura y huérfanos
 *  5. Activar las 10 Reglas Guardián
 *
 * Diseñado para no usar consola del navegador.
 */
const VideoToolsPage = () => {
  const [loading, setLoading] = useState({});
  const [results, setResults] = useState({});
  const [expanded, setExpanded] = useState({});

  const run = async (key, label, method, url, body) => {
    if (loading[key]) return;
    setLoading((p) => ({ ...p, [key]: true }));
    try {
      const config = {};
      const res = method === 'POST'
        ? await axios.post(`${API}${url}`, body || {}, config)
        : await axios.get(`${API}${url}`, config);
      setResults((p) => ({ ...p, [key]: { ok: true, data: res.data, ts: new Date() } }));
      setExpanded((p) => ({ ...p, [key]: true }));
      toast.success(`${label} completado ✅`);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || 'Error';
      setResults((p) => ({ ...p, [key]: { ok: false, error: detail, ts: new Date() } }));
      setExpanded((p) => ({ ...p, [key]: true }));
      toast.error(detail);
    } finally {
      setLoading((p) => ({ ...p, [key]: false }));
    }
  };

  const tools = [
    {
      key: 'migrate-r2',
      icon: Cloud,
      title: '1. Migrar videos a R2 (persistente)',
      desc: 'Sube todos los videos del disco local a Cloudflare R2 para que sobrevivan a futuros deploys. Idempotente: los que ya están, los saltea.',
      method: 'POST',
      url: '/admin/migrate-videos-to-r2',
      cta: 'Migrar a R2 ahora',
      color: 'from-blue-500/15 to-blue-500/5 border-blue-500/30',
    },
    {
      key: 'backfill-ids',
      icon: Database,
      title: '2. Reparar planes viejos',
      desc: 'Rellena el campo exercise_id en los planes que se crearon antes del nuevo sistema de IDs. Esto permite que la propagación de videos sea fiable.',
      method: 'POST',
      url: '/admin/backfill-exercise-ids-in-plans',
      cta: 'Reparar planes',
      color: 'from-purple-500/15 to-purple-500/5 border-purple-500/30',
    },
    {
      key: 'propagate',
      icon: RefreshCw,
      title: '3. Propagar videos a todos los planes',
      desc: 'Asigna a cada ejercicio dentro de cada plan el video correcto desde la biblioteca. Matching robusto (sin tildes, sin signos).',
      method: 'POST',
      url: '/admin/propagate-videos-to-plans',
      cta: 'Propagar videos',
      color: 'from-emerald-500/15 to-emerald-500/5 border-emerald-500/30',
    },
    {
      key: 'coverage',
      icon: BarChart3,
      title: '4. Ver cobertura y huérfanos',
      desc: 'Muestra el % de ejercicios con video y cuáles son los nombres "huérfanos" (en planes pero sin video correspondiente). Esto es lo que tienes que limpiar.',
      method: 'GET',
      url: '/admin/plan-video-coverage',
      cta: 'Ver cobertura',
      color: 'from-amber-500/15 to-amber-500/5 border-amber-500/30',
    },
    {
      key: 'seed-rules',
      icon: Sparkles,
      title: '5. Activar las 10 Reglas Guardián',
      desc: 'Crea las 8 píldoras pedagógicas y las 11 reglas (primera sesión, 1 semana, mesociclo, PR, cumpleaños, RPE alto, etc.). Idempotente.',
      method: 'POST',
      url: '/admin/seed-guardian-rules',
      cta: 'Activar Reglas Guardián',
      color: 'from-pink-500/15 to-pink-500/5 border-pink-500/30',
    },
  ];

  const renderResult = (key, result) => {
    if (!result) return null;
    if (!result.ok) {
      return (
        <div className="mt-3 p-3 rounded-md bg-red-500/10 border border-red-500/30 text-sm text-red-300">
          <AlertTriangle className="inline h-4 w-4 mr-1" /> Error: {result.error}
        </div>
      );
    }
    const d = result.data;
    // Coverage especial: render bonito
    if (key === 'coverage' && d.total_plans !== undefined) {
      const pct = d.coverage_percent || 0;
      const barColor = pct >= 90 ? 'bg-emerald-500' : pct >= 70 ? 'bg-amber-500' : 'bg-red-500';
      return (
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="p-2 bg-zinc-900 rounded-md">
              <div className="text-2xl font-bold text-foreground">{d.total_plans}</div>
              <div className="text-xs text-muted-foreground">Planes</div>
            </div>
            <div className="p-2 bg-zinc-900 rounded-md">
              <div className="text-2xl font-bold text-emerald-400">{d.exercises_with_video}</div>
              <div className="text-xs text-muted-foreground">Con video</div>
            </div>
            <div className="p-2 bg-zinc-900 rounded-md">
              <div className="text-2xl font-bold text-red-400">{d.exercises_without_video}</div>
              <div className="text-xs text-muted-foreground">Sin video</div>
            </div>
          </div>
          <div>
            <div className="flex justify-between mb-1 text-sm">
              <span className="text-muted-foreground">Cobertura</span>
              <span className="font-bold text-foreground">{pct}%</span>
            </div>
            <div className="h-3 bg-zinc-900 rounded-full overflow-hidden">
              <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
            </div>
          </div>
          {d.top_missing && d.top_missing.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-foreground mb-2">Top {Math.min(d.top_missing.length, 30)} ejercicios sin video:</p>
              <div className="space-y-1 max-h-80 overflow-y-auto">
                {d.top_missing.map((m, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2 bg-zinc-900/60 rounded-md text-sm">
                    <span className="text-foreground truncate flex-1 mr-2">{m.name}</span>
                    <Badge variant="outline" className="border-red-500/40 text-red-400 text-xs">
                      en {m.count} planes
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }
    // Render genérico
    return (
      <pre className="mt-3 p-3 rounded-md bg-zinc-900 border border-zinc-800 text-xs text-emerald-300 overflow-x-auto max-h-72">
        {JSON.stringify(d, null, 2)}
      </pre>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-4xl mx-auto" data-testid="video-tools-page">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Herramientas de Mantenimiento</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Operaciones de un solo click. Ejecuta en orden (1 → 5) tras cada Deploy importante o cuando subas videos nuevos.
          </p>
        </div>

        <div className="space-y-3">
          {tools.map((t) => {
            const Icon = t.icon;
            const isLoading = loading[t.key];
            const r = results[t.key];
            const isExp = expanded[t.key];
            return (
              <Card key={t.key} className={`bg-gradient-to-br ${t.color}`} data-testid={`tool-card-${t.key}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="p-2 bg-zinc-900/80 rounded-lg flex-shrink-0">
                        <Icon className="h-5 w-5 text-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-base text-foreground">{t.title}</CardTitle>
                        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{t.desc}</p>
                      </div>
                    </div>
                    {r && (
                      <button
                        onClick={() => setExpanded((p) => ({ ...p, [t.key]: !isExp }))}
                        className="p-1 text-muted-foreground hover:text-foreground"
                        title={isExp ? 'Ocultar resultado' : 'Mostrar resultado'}
                      >
                        {isExp ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      onClick={() => run(t.key, t.cta, t.method, t.url)}
                      disabled={isLoading}
                      data-testid={`tool-run-${t.key}`}
                      className="bg-foreground text-background hover:bg-foreground/90"
                    >
                      {isLoading ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Ejecutando...</>
                      ) : (
                        <><CheckCircle2 className="h-4 w-4 mr-2" /> {t.cta}</>
                      )}
                    </Button>
                    {r && r.ok && (
                      <span className="text-xs text-emerald-400 flex items-center gap-1">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Listo &middot; {r.ts.toLocaleTimeString('es-ES')}
                      </span>
                    )}
                  </div>
                  {isExp && renderResult(t.key, r)}
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Card className="bg-zinc-900/40 border-zinc-800">
          <CardContent className="p-4 text-xs text-muted-foreground space-y-2">
            <p><strong className="text-foreground">Orden recomendado:</strong> 1 → 2 → 3 → 4 → 5</p>
            <p>El 4 te dice qué te falta. Los nombres de la lista "Top sin video" son <strong>ejercicios en planes que no matchean con la biblioteca</strong>. Para cada uno tienes 3 opciones:</p>
            <ul className="list-disc pl-5 space-y-0.5">
              <li><strong>Renombrar</strong> en el plan al nombre exacto de la biblioteca</li>
              <li><strong>Subir</strong> a la biblioteca con video desde <code className="px-1 bg-zinc-800 rounded">/admin/exercises</code></li>
              <li><strong>Quitar</strong> del plan si ya no lo usás</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default VideoToolsPage;
